import { ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { MediaDetailDialog } from '@/components/media-detail-dialog';
import { MediaDialog, type MediaValues } from '@/components/media-dialog';
import { MediaThumbnail } from '@/components/media-thumbnail';
import { MediaUploadDialog } from '@/components/media-upload-dialog';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMedia } from '@/lib/api-hooks';
import {
  MEDIA_PAGE_SIZE_OPTIONS,
  type MediaItem,
  type MediaOrderKey,
  type MediaType,
  deleteMedia,
  mediaSizeLabel,
  mediaTypes,
  updateMedia,
} from '@/lib/media';
import { useMediaStore } from '@/lib/media-store';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

const orderOptions: Array<{ value: MediaOrderKey; label: string }> = [
  { value: 'newest', label: 'Recently updated' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'size', label: 'Largest first' },
];

const typeFilters: Array<'all' | MediaType> = ['all', ...mediaTypes];

export function MediaPage() {
  const search = useMediaStore((state) => state.search);
  const type = useMediaStore((state) => state.type);
  const order = useMediaStore((state) => state.order);
  const page = useMediaStore((state) => state.page);
  const pageSize = useMediaStore((state) => state.pageSize);
  const setSearch = useMediaStore((state) => state.setSearch);
  const setType = useMediaStore((state) => state.setType);
  const setOrder = useMediaStore((state) => state.setOrder);
  const setPage = useMediaStore((state) => state.setPage);
  const setPageSize = useMediaStore((state) => state.setPageSize);

  const { data, isLoading } = useMedia({ search, type, order, page, pageSize });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState<MediaItem | null>(null);
  const [viewing, setViewing] = useState<MediaItem | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const skeletonKeys = Array.from({ length: pageSize }, (_, index) => `media-skeleton-${index}`);

  function openEdit(item: MediaItem) {
    setEditing(item);
  }

  async function handleSubmit(values: MediaValues) {
    if (!editing) return;
    await updateMedia(editing.id, values);
    invalidateResource('media');
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search media"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-muted-foreground text-sm sm:inline">Order by</span>
          <Select value={order} onValueChange={(value) => setOrder(value as MediaOrderKey)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Plus />
            Upload
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {typeFilters.map((filter) => (
          <Button
            key={filter}
            size="sm"
            variant={type === filter ? 'default' : 'ghost'}
            onClick={() => setType(filter)}
            className="h-8 capitalize"
          >
            {filter}
          </Button>
        ))}
      </div>

      <ScrollArea>
        {isLoading && items.length === 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {skeletonKeys.map((key) => (
              <div key={key} className="overflow-hidden rounded-md border bg-card">
                <div className="aspect-video w-full animate-pulse bg-muted" />
                <div className="space-y-2 p-3">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm">
            No media match your filters.
          </p>
        ) : (
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
              isLoading && 'opacity-60 transition-opacity',
            )}
          >
            {items.map((item) => (
              <div key={item.id} className="group overflow-hidden rounded-md border bg-card">
                <button type="button" onClick={() => setViewing(item)} className="block w-full">
                  <MediaThumbnail item={item} className="aspect-video w-full" />
                </button>
                <div className="space-y-2 p-3">
                  <div className="space-y-0.5">
                    <button
                      type="button"
                      onClick={() => setViewing(item)}
                      className="block max-w-full truncate text-left font-medium text-sm hover:underline"
                    >
                      {item.title || item.filename}
                    </button>
                    <p className="text-muted-foreground text-xs">
                      {item.usageCount === 0
                        ? 'Unused'
                        : `${item.usageCount} usage${item.usageCount === 1 ? '' : 's'}`}
                      {' · '}
                      <span className="capitalize">{item.type}</span> · {mediaSizeLabel(item)}
                    </p>
                  </div>
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => openEdit(item)}
                    >
                      <Pencil className="size-3.5" />
                      <span className="sr-only">Edit {item.filename}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-destructive hover:text-destructive"
                      onClick={() => setDeleting(item)}
                    >
                      <Trash2 className="size-3.5" />
                      <span className="sr-only">Delete {item.filename}</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Items per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>

      <MediaUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={(uploaded) => setViewing(uploaded[0] ?? null)}
      />

      {editing && (
        <MediaDialog
          key={editing.id}
          open
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          initialValues={{ title: editing.title, alt: editing.alt, tags: editing.tags }}
          onSubmit={handleSubmit}
        />
      )}

      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleting(null);
          }}
          title="Delete media?"
          description={`"${deleting.filename}" will be permanently removed from the library.`}
          confirmLabel="Delete"
          onConfirm={async () => {
            await deleteMedia(deleting.id);
            invalidateResource('media');
          }}
        />
      )}

      <MediaDetailDialog
        key={viewing?.id ?? 'none'}
        media={viewing}
        open={Boolean(viewing)}
        onOpenChange={(next) => {
          if (!next) setViewing(null);
        }}
      />
    </div>
  );
}
